import React, { useState, useEffect, useCallback } from 'react';
import {
  getRejectedPayments, createRejectedPayment,
  updateRejectedPaymentStatus, addRejectedPaymentContact,
  deleteRejectedPayment, syncRejectedPaymentsVpos
} from '../lib/api';
import { formatCurrency, formatDate, getTodayDate } from '../lib/utils';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Plus, CreditCard, Phone, Loader2, AlertTriangle,
  CheckCircle, XCircle, History, RefreshCw, Clock, Zap, Trash2
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

const contactResults = [
  { value: 'contesto_pagara', label: 'Contesto - Pagara' },
  { value: 'contesto_problema_tarjeta', label: 'Contesto - Problema de Tarjeta' },
  { value: 'no_contesto', label: 'No Contesto' },
  { value: 'mensaje_dejado', label: 'Mensaje Dejado' },
  { value: 'pago_realizado', label: 'Pago Realizado' },
  { value: 'solicita_cancelacion', label: 'Solicita Cancelacion' },
];

const PagosRechazados = () => {
  const { isDirection } = useAuth();
  const [payments, setPayments] = useState([]);
  const [archivedPayments, setArchivedPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [showNewDialog, setShowNewDialog] = useState(false);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [showContactDialog, setShowContactDialog] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [vposSummary, setVposSummary] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    client_name: '', plan: '', amount: '', rejection_date: getTodayDate()
  });
  const [contactData, setContactData] = useState({ result: '', observations: '' });

  const fetchPayments = useCallback(async () => {
    try {
      const [activeRes, summaryRes] = await Promise.all([
        getRejectedPayments({ is_active: true }),
        api.get('/rejected-payments/vpos-summary')
      ]);
      setPayments(activeRes.data);
      setVposSummary(summaryRes.data);
    } catch (error) {
      
    } finally { setLoading(false); }
  }, []);

  const fetchArchived = async () => {
    try {
      const res = await getRejectedPayments({ is_active: false });
      setArchivedPayments(res.data);
      setShowArchived(true);
    } catch (error) {
      toast.error('Error al cargar historial');
    }
  };

  useEffect(() => { fetchPayments(); }, [fetchPayments]);

  const handleSync = async () => {
    setSyncing(true);
    try {
      const res = await syncRejectedPaymentsVpos();
      if (res.data.error) {
        toast.error(res.data.error);
      } else {
        toast.success(`Sync completado: ${res.data.nuevos || 0} nuevos, ${res.data.archivados || 0} regularizados`);
        fetchPayments();
      }
    } catch (error) {
      toast.error('Error al sincronizar con Virtual POS');
    } finally { setSyncing(false); }
  };

  const handleNewPayment = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await createRejectedPayment({
        client_name: formData.client_name,
        plan: formData.plan,
        monto: parseFloat(formData.amount),
        fecha_rechazo: formData.rejection_date
      });
      toast.success('Pago rechazado registrado');
      setShowNewDialog(false);
      setFormData({ client_name: '', plan: '', amount: '', rejection_date: getTodayDate() });
      fetchPayments();
    } catch (error) {
      toast.error('Error al registrar pago');
    } finally { setSaving(false); }
  };

  const handleStatusChange = async (paymentId, newStatus) => {
    try {
      await updateRejectedPaymentStatus(paymentId, newStatus, '');
      toast.success('Estado actualizado');
      fetchPayments();
    } catch (error) {
      toast.error('Error al actualizar estado');
    }
  };

  const handleAddContact = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await addRejectedPaymentContact(selectedPayment.id, contactData.result, contactData.observations);
      toast.success('Contacto registrado');
      setShowContactDialog(false);
      setContactData({ result: '', observations: '' });
      fetchPayments();
    } catch (error) {
      toast.error('Error al registrar contacto');
    } finally { setSaving(false); }
  };

  const handleDeletePayment = async (payment) => {
    if (!window.confirm(`Eliminar pago rechazado de "${payment.client_name}"? Esta accion no se puede deshacer.`)) return;
    try {
      await deleteRejectedPayment(payment.id);
      toast.success('Pago eliminado');
      fetchPayments();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al eliminar');
    }
  };

  const openDetail = (payment) => {
    setSelectedPayment(payment);
    setShowDetailDialog(true);
  };

  const totalRechazado = payments.reduce((sum, p) => sum + (p.monto || 0), 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full" data-testid="payments-loading">
        <Loader2 className="h-8 w-8 animate-spin text-[#2B4257]" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="pagos-rechazados-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="page-title">Pagos Rechazados</h1>
          <p className="page-subtitle">
            Cola en vivo desde Virtual POS
            {vposSummary?.last_sync && (
              <span className="ml-2 text-xs text-[#5C7083]">
                Ultimo sync: {new Date(vposSummary.last_sync).toLocaleString('es-CL')}
              </span>
            )}
          </p>
        </div>
        <div className="flex gap-2">
          {isDirection() && (
            <Button className="btn-secondary gap-2" onClick={handleSync} disabled={syncing} data-testid="btn-sync-vpos">
              {syncing ? <Loader2 size={16} className="animate-spin" /> : <RefreshCw size={16} />}
              {syncing ? 'Sincronizando...' : 'Sync VPos'}
            </Button>
          )}
          <Button className="btn-secondary gap-2" onClick={fetchArchived} data-testid="btn-show-archived">
            <History size={16} /> Historial
          </Button>
          <Button className="btn-primary gap-2" onClick={() => setShowNewDialog(true)} data-testid="btn-new-payment">
            <Plus size={16} /> Manual
          </Button>
        </div>
      </div>

      {/* Summary Cards - estilo VPos */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="vpos-summary">
        <div className="card p-4 border-l-4 border-l-[#C25953]">
          <div className="flex items-center gap-2 mb-1">
            <AlertTriangle className="text-[#C25953]" size={18} />
            <span className="text-xs text-[#5C7083]">Rechazados</span>
          </div>
          <div className="text-2xl font-bold text-[#C25953]">{payments.length}</div>
          <div className="text-sm text-[#5C7083]">{formatCurrency(totalRechazado)}</div>
        </div>
        <div className="card p-4 border-l-4 border-l-[#D98A6C]">
          <div className="flex items-center gap-2 mb-1">
            <Phone className="text-[#D98A6C]" size={18} />
            <span className="text-xs text-[#5C7083]">Pendientes Contacto</span>
          </div>
          <div className="text-2xl font-bold text-[#D98A6C]">
            {payments.filter(p => !p.contact_attempts?.length).length}
          </div>
          <div className="text-sm text-[#5C7083]">Sin contactar</div>
        </div>
        <div className="card p-4 border-l-4 border-l-[#2B4257]">
          <div className="flex items-center gap-2 mb-1">
            <Clock className="text-[#2B4257]" size={18} />
            <span className="text-xs text-[#5C7083]">Contactados</span>
          </div>
          <div className="text-2xl font-bold text-[#2B4257]">
            {payments.filter(p => p.contact_attempts?.length > 0).length}
          </div>
          <div className="text-sm text-[#5C7083]">En seguimiento</div>
        </div>
        <div className="card p-4 border-l-4 border-l-[#4A7C59]">
          <div className="flex items-center gap-2 mb-1">
            <Zap className="text-[#4A7C59]" size={18} />
            <span className="text-xs text-[#5C7083]">Sync Automatico</span>
          </div>
          <div className="text-lg font-bold text-[#4A7C59]">Cada 15 min</div>
          <div className="text-sm text-[#5C7083]">Espejo Virtual POS</div>
        </div>
      </div>

      {/* Info banner */}
      <div className="p-3 bg-[#F5F0E8] rounded-lg flex items-center gap-3" data-testid="sync-info">
        <RefreshCw size={16} className="text-[#2B4257] flex-shrink-0" />
        <p className="text-sm text-[#5C7083]">
          Esta tabla se sincroniza automaticamente con Virtual POS cada 15 minutos.
          Cuando un pago se regulariza en VPos, desaparece automaticamente de aqui.
        </p>
      </div>

      {/* Active Rejected Payments Table */}
      <div className="card" data-testid="payments-table">
        <h3 className="text-lg font-semibold text-[#1A2835] mb-4">
          Cargos Rechazados Activos
          <span className="text-sm font-normal text-[#5C7083] ml-2">({payments.length})</span>
        </h3>
        {payments.length === 0 ? (
          <div className="text-center py-12">
            <CheckCircle className="mx-auto h-16 w-16 text-[#4A7C59] mb-4" />
            <h3 className="text-lg font-semibold text-[#4A7C59]">Sin pagos rechazados</h3>
            <p className="text-[#5C7083] mt-2">No hay cargos rechazados activos en Virtual POS</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="data-table w-full text-sm">
              <thead>
                <tr>
                  <th>Cliente</th>
                  <th>Plan</th>
                  <th className="text-right">Monto</th>
                  <th>Cargo</th>
                  <th>Fecha Rechazo</th>
                  <th>Seguimiento</th>
                  <th>Contactos</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {payments.map((p) => (
                  <tr key={p.id} className="hover:bg-[#F5F0E8]/50" data-testid={`payment-row-${p.id}`}>
                    <td className="font-medium">{p.client_name}</td>
                    <td className="text-[#5C7083]">{p.plan || '-'}</td>
                    <td className="text-right font-semibold text-[#C25953]">{formatCurrency(p.monto)}</td>
                    <td className="text-[#5C7083]">{p.cargo_numero || '-'}</td>
                    <td>{p.fecha_rechazo ? formatDate(p.fecha_rechazo) : '-'}</td>
                    <td>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                        p.estado_seguimiento === 'pendiente' || p.status === 'pendiente'
                          ? 'bg-amber-100 text-amber-700'
                          : p.estado_seguimiento === 'recuperado' || p.status === 'regularizado'
                          ? 'bg-green-100 text-green-700'
                          : 'bg-blue-100 text-blue-700'
                      }`}>
                        {p.estado_seguimiento || p.status || 'pendiente'}
                      </span>
                    </td>
                    <td className="text-center">
                      <span className={`text-xs font-medium ${(p.contact_attempts?.length || 0) > 0 ? 'text-[#2B4257]' : 'text-[#C25953]'}`}>
                        {p.contact_attempts?.length || 0}
                      </span>
                    </td>
                    <td>
                      <div className="flex items-center gap-1">
                        <Button variant="ghost" size="sm" className="btn-ghost p-2"
                          onClick={() => openDetail(p)} title="Ver detalle"
                          data-testid={`btn-detail-${p.id}`}>
                          <CreditCard size={16} />
                        </Button>
                        <Button variant="ghost" size="sm" className="btn-ghost p-2"
                          onClick={() => { setSelectedPayment(p); setShowContactDialog(true); }}
                          title="Registrar contacto"
                          data-testid={`btn-contact-${p.id}`}>
                          <Phone size={16} />
                        </Button>
                        {isDirection() && (
                          <Button variant="ghost" size="sm"
                            className="btn-ghost p-2 text-[#C25953] hover:text-[#A63D3D] hover:bg-[#FBEAE9]"
                            onClick={() => handleDeletePayment(p)}
                            data-testid={`btn-delete-payment-${p.id}`}>
                            <Trash2 size={16} />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Archived/Regularized Payments */}
      {showArchived && (
        <div className="card" data-testid="archived-payments">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-[#1A2835]">
              Historial (Regularizados)
              <span className="text-sm font-normal text-[#5C7083] ml-2">({archivedPayments.length})</span>
            </h3>
            <Button variant="ghost" className="btn-ghost" onClick={() => setShowArchived(false)}>
              <XCircle size={16} />
            </Button>
          </div>
          {archivedPayments.length === 0 ? (
            <p className="text-center py-8 text-[#5C7083]">No hay registros archivados</p>
          ) : (
            <div className="overflow-x-auto max-h-[300px] overflow-y-auto">
              <table className="data-table w-full text-sm">
                <thead>
                  <tr>
                    <th>Cliente</th>
                    <th>Plan</th>
                    <th className="text-right">Monto</th>
                    <th>Fecha Rechazo</th>
                    <th>Estado</th>
                  </tr>
                </thead>
                <tbody>
                  {archivedPayments.map((p) => (
                    <tr key={p.id} className="opacity-60">
                      <td>{p.client_name}</td>
                      <td>{p.plan || '-'}</td>
                      <td className="text-right">{formatCurrency(p.monto)}</td>
                      <td>{p.fecha_rechazo ? formatDate(p.fecha_rechazo) : '-'}</td>
                      <td><span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700">{p.status || 'regularizado'}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* New Manual Payment Dialog */}
      <Dialog open={showNewDialog} onOpenChange={setShowNewDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Registrar Pago Rechazado Manual</DialogTitle></DialogHeader>
          <form onSubmit={handleNewPayment} className="space-y-4" data-testid="new-payment-form">
            <div>
              <Label className="form-label">Cliente *</Label>
              <Input value={formData.client_name}
                onChange={(e) => setFormData({ ...formData, client_name: e.target.value })}
                required data-testid="input-payment-client" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="form-label">Plan</Label>
                <Input value={formData.plan}
                  onChange={(e) => setFormData({ ...formData, plan: e.target.value })}
                  data-testid="input-payment-plan" />
              </div>
              <div>
                <Label className="form-label">Monto *</Label>
                <Input type="number" value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                  required data-testid="input-payment-amount" />
              </div>
            </div>
            <div>
              <Label className="form-label">Fecha Rechazo</Label>
              <Input type="date" value={formData.rejection_date}
                onChange={(e) => setFormData({ ...formData, rejection_date: e.target.value })}
                data-testid="input-payment-date" />
            </div>
            <div className="flex justify-end gap-3">
              <Button type="button" variant="outline" onClick={() => setShowNewDialog(false)} className="btn-secondary">Cancelar</Button>
              <Button type="submit" className="btn-primary" disabled={saving} data-testid="btn-save-payment">
                {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />} Registrar
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Detail Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Detalle del Pago Rechazado</DialogTitle></DialogHeader>
          {selectedPayment && (
            <div className="space-y-4" data-testid="payment-detail">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <p className="text-[#5C7083]">Cliente</p>
                  <p className="font-semibold">{selectedPayment.client_name}</p>
                </div>
                <div>
                  <p className="text-[#5C7083]">Monto</p>
                  <p className="font-semibold text-[#C25953]">{formatCurrency(selectedPayment.monto)}</p>
                </div>
                <div>
                  <p className="text-[#5C7083]">Plan</p>
                  <p>{selectedPayment.plan || '-'}</p>
                </div>
                <div>
                  <p className="text-[#5C7083]">Cargo</p>
                  <p>{selectedPayment.cargo_numero || '-'}</p>
                </div>
                <div>
                  <p className="text-[#5C7083]">Fecha Rechazo</p>
                  <p>{selectedPayment.fecha_rechazo ? formatDate(selectedPayment.fecha_rechazo) : '-'}</p>
                </div>
                <div>
                  <p className="text-[#5C7083]">Seguimiento</p>
                  <p>{selectedPayment.estado_seguimiento || selectedPayment.status || 'pendiente'}</p>
                </div>
              </div>

              {/* Contact History */}
              {selectedPayment.contact_attempts?.length > 0 && (
                <div>
                  <h4 className="font-semibold text-[#1A2835] mb-2">Historial de Contactos</h4>
                  <div className="space-y-2 max-h-[200px] overflow-y-auto">
                    {selectedPayment.contact_attempts.map((c, i) => (
                      <div key={`${c.date}-${i}`} className="p-2 bg-[#F5F0E8] rounded-lg text-sm">
                        <div className="flex justify-between">
                          <span className="font-medium">{c.result}</span>
                          <span className="text-xs text-[#5C7083]">{formatDate(c.date)}</span>
                        </div>
                        {c.observations && <p className="text-[#5C7083] mt-1">{c.observations}</p>}
                        {c.contacted_by_name && <p className="text-xs text-[#5C7083]">Por: {c.contacted_by_name}</p>}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setShowDetailDialog(false)} className="btn-secondary">Cerrar</Button>
                <Button className="btn-primary gap-2"
                  onClick={() => { setShowDetailDialog(false); setShowContactDialog(true); }}>
                  <Phone size={16} /> Registrar Contacto
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Contact Dialog */}
      <Dialog open={showContactDialog} onOpenChange={setShowContactDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Registrar Contacto</DialogTitle></DialogHeader>
          {selectedPayment && (
            <form onSubmit={handleAddContact} className="space-y-4" data-testid="contact-form">
              <div className="p-3 bg-[#F5F0E8] rounded-lg">
                <p className="font-medium">{selectedPayment.client_name}</p>
                <p className="text-sm text-[#C25953]">{formatCurrency(selectedPayment.monto)} - {selectedPayment.plan || 'Sin plan'}</p>
              </div>
              <div>
                <Label className="form-label">Resultado del contacto *</Label>
                <Select value={contactData.result || undefined}
                  onValueChange={(v) => setContactData({ ...contactData, result: v })}>
                  <SelectTrigger data-testid="select-contact-result"><SelectValue placeholder="Seleccionar resultado" /></SelectTrigger>
                  <SelectContent>
                    {contactResults.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="form-label">Observaciones</Label>
                <textarea value={contactData.observations}
                  onChange={(e) => setContactData({ ...contactData, observations: e.target.value })}
                  className="form-input min-h-[80px] resize-none w-full"
                  placeholder="Detalles del contacto..."
                  data-testid="input-contact-observations" />
              </div>
              <div className="flex justify-end gap-3">
                <Button type="button" variant="outline" onClick={() => setShowContactDialog(false)} className="btn-secondary">Cancelar</Button>
                <Button type="submit" className="btn-primary" disabled={saving || !contactData.result} data-testid="btn-save-contact">
                  {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />} Guardar
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PagosRechazados;
